import { useStdin } from 'ink';
import { useState, useEffect, type ReactNode } from 'react';
import isCi from 'is-ci';

// components
import Login from './Login';

// logic
import { signInWithCustomToken } from '../utilities/firestore';
import {
  getAuthConfig,
  setAuthConfig,
  setEphemeralMode,
} from '../utilities/configStore';
import postToFirebaseFunction from '../utilities/postToFirebaseFunction';
import getEnvironmentVariables from '../utilities/getEnvironmentVariables';
import ErrorDisplay from './ErrorDisplay';
import LoadingText from './LoadingText';

export default function LoginHOC({
  children,
  isInteractive = true,
}: {
  children?: ReactNode;
  isInteractive?: boolean;
}) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isEffectDone, setEffectDone] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { isRawModeSupported } = useStdin();

  // use effect for determining if tokens exist and are valid
  useEffect(() => {
    async function isAccessTokenValid() {
      const { accessToken, email, jwtToken } = getAuthConfig();
      let userCreds = null;

      const { TODESKTOP_ACCESS_TOKEN, TODESKTOP_EMAIL } =
        getEnvironmentVariables();

      // 1. attempt log in with environment variables if present
      try {
        if (TODESKTOP_ACCESS_TOKEN && TODESKTOP_EMAIL) {
          const newJwtToken = await postToFirebaseFunction(
            'loginWithAccessToken',
            {
              email: TODESKTOP_EMAIL,
              accessToken: TODESKTOP_ACCESS_TOKEN,
            },
          );

          if (newJwtToken) {
            userCreds = await signInWithCustomToken(newJwtToken);
            if (userCreds.user) {
              setEphemeralMode(true);
              setAuthConfig(
                TODESKTOP_EMAIL,
                TODESKTOP_ACCESS_TOKEN,
                newJwtToken,
              );
              setIsLoggedIn(true);
            }
          }
          // if no environment variables set and is in a CI, set alert message and exit
        } else if (!isRawModeSupported || isCi) {
          setError(
            new Error(
              'ToDesktop environment variables not found. When automating builds with your CI provider, you need to specify environment variables for TODESKTOP_EMAIL and TODESKTOP_ACCESS_TOKEN.',
            ),
          );
        }
      } catch (err) {
        if (typeof err === 'object' && err !== null) {
          (err as Error).message = 'Log in with environment variables failed.';
        }
        setError(err as Error);
      }

      // 2. attempt log in with jwtToken if present
      try {
        if (!userCreds && jwtToken) {
          userCreds = await signInWithCustomToken(jwtToken);
          if (userCreds.user) setIsLoggedIn(true);
        }
      } catch {
        // do nothing, attempt login with accessToken and email
      }

      // 3. attempt log in with accessToken and email on disk
      try {
        if (!userCreds && accessToken && email) {
          const newJwtToken = await postToFirebaseFunction(
            'loginWithAccessToken',
            { email, accessToken },
          );

          if (newJwtToken) {
            userCreds = await signInWithCustomToken(newJwtToken);
            setAuthConfig(email, accessToken, newJwtToken);
            if (userCreds.user) setIsLoggedIn(true);
          }
        }
      } catch {
        // do nothing: no error state and not logged in state means render login
      }

      setEffectDone(true);
    }

    isAccessTokenValid();
  }, [isRawModeSupported]);

  if (error && isInteractive) {
    return <ErrorDisplay error={error} />;
  }

  if (!isEffectDone) {
    return <LoadingText />;
  }

  if (!isLoggedIn && isInteractive) {
    return <Login setIsLoggedIn={setIsLoggedIn} />;
  }

  return <>{children}</>;
}
